import React, { useState, useEffect, useRef } from "react";

function get(obj, path, fallback) {
  var result = obj;
  var parts = path.split(".");
  for (var i = 0; i < parts.length; i++) {
    if (result === null || result === undefined) return fallback;
    result = result[parts[i]];
  }
  return result === undefined || result === null ? fallback : result;
}

function cleanHtmlTags(val) {
  if (typeof val === "string") {
    return val.replace(/<\/?p>/gi, " ").trim();
  }
  if (Array.isArray(val)) {
    return val.map(item => cleanHtmlTags(item));
  }
  if (val !== null && typeof val === "object") {
    const cleaned = {};
    for (let k in val) cleaned[k] = cleanHtmlTags(val[k]);
    return cleaned;
  }
  return val;
}

function getInitialValue(val) {
  if (val === null || val === undefined) return "";
  return cleanHtmlTags(val);
}

function setDeep(obj, path, newValue) {
  const root = Array.isArray(obj) ? [...obj] : { ...obj };
  let current = root;
  for (let i = 0; i < path.length - 1; i++) {
    const key = path[i];
    current[key] = Array.isArray(current[key]) ? [...current[key]] : { ...current[key] };
    current = current[key];
  }
  current[path[path.length - 1]] = newValue;
  return root;
}

const AutoResizeTextarea = ({ value, onChange, readOnly, style, placeholder }) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      const extraRowHeight = 18;
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight + extraRowHeight}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={textareaRef}
      style={{
        ...style,
        resize: "none",
        overflow: "hidden",
        boxSizing: "border-box",
        fontFamily: "inherit",
        lineHeight: "1.4"
      }}
      value={value}
      onChange={onChange}
      readOnly={readOnly}
      placeholder={placeholder}
      rows={1}
    />
  );
};

function AmazonProductListingEditableField({ field = {}, onFieldChange, changedValue, isUpdated }) {
  var rawValue = isUpdated ? changedValue : get(field, "listing.value", null);
  var initial = getInitialValue(rawValue);

  var [value, setValue] = useState(initial);

  var options = get(field, "schema.allowed_values", []) || [];

  var allowCustomValues =
    get(field, "schema.rules.allow_custom_values", false);

  var isDropdown =
    get(field, "ui.input_type", "") === "dropdown";
  var rules =
    get(field, "schema.rules", {}) || {};

  const isArrayField =
    rules.field_type === "array";

  const isMultiValueField =
    isArrayField &&
    (
      rules.maxItems > 1 ||
      rules.maxUniqueItems > 1
    );
  var isSuggestiveField =
    options.length > 0 &&
    allowCustomValues &&
    !isMultiValueField;
  var isEditable = get(field, "schema.editable", true);

  var examples = get(field, "schema.examples", []) || [];

  var fieldType = rules.type;

  var actualFieldType = fieldType;

  var maxLength =
    actualFieldType === "string"
      ? rules.maxLength
      : null;

  var maxUtf8ByteLength =
    rules.maxUtf8ByteLength;
  var minLength = rules.minLength;
  var minimum = rules.minimum;
  var maximum = rules.maximum;
  var exclusiveMinimum =
    rules.exclusiveMinimum;

  var exclusiveMaximum =
    rules.exclusiveMaximum;
  var multipleOf =
    rules.multipleOf;
  var decimalPlaces = null;

  if (multipleOf) {

    const multipleString =
      Number(multipleOf)
        .toFixed(10);

    const decimalPart =
      multipleString
        .split(".")[1];

    if (decimalPart) {

      decimalPlaces =
        decimalPart
          .replace(/0+$/, "")
          .length;
    }
  }
  var isNumberField =
    (
      actualFieldType === "number" ||
      actualFieldType === "integer"
    ) &&
    !Array.isArray(value) &&
    typeof value !== "object";

  var isDateField =
    rules.format === "date" ||
    rules.format === "date-time" ||
    get(field, "ui.format", "") === "date" ||
    get(field, "ui.format", "") === "date-time";

  var placeholder =
    actualFieldType === "boolean"
      ? "Select"
      : (
        examples.length > 0
          ? `e.g. ${examples[0]}`
          : ""
      );

  var currentLength =
    typeof value === "string"
      ? value.length
      : 0;
  var currentUtf8Length =
    typeof value === "string"
      ? new TextEncoder().encode(value).length
      : 0;
  var isTooLong =

    (
      maxLength &&
      currentLength > maxLength
    )

    ||

    (
      maxUtf8ByteLength &&
      currentUtf8Length >
      maxUtf8ByteLength
    );
  var numericValue = Number(value);

  var isNumberInvalid =
    isNumberField &&
    value !== "" &&
    (
      (minimum !== undefined &&
        numericValue < minimum) ||

      (maximum !== undefined &&
        numericValue > maximum) ||

      (
        exclusiveMinimum !== undefined &&
        numericValue <= exclusiveMinimum
      ) ||

      (
        exclusiveMaximum !== undefined &&
        numericValue >= exclusiveMaximum
      )
    );
  var isDateInvalid =
    isDateField &&
    value &&
    (
      !/^\d{4}-\d{2}-\d{2}$/.test(value) ||
      new Date(value).toISOString().slice(0, 10) !== value
    );
  var isTooShort =
    minLength &&
    currentLength < minLength &&
    currentLength > 0;

  var inputStyle = {
    width: "100%",
    fontSize: 12,
    padding: "4px 8px",
    borderRadius: 6,
    border:
      isTooLong ||
        isTooShort ||
        isNumberInvalid ||
        isDateInvalid
        ? "1px solid red"
        : "1px solid #ccc",
    background: "var(--color-background-primary, #ffffff)",
    color: "#333",
    cursor: "text"
  };

  var plainTextStyle = {
    width: "100%",
    fontSize: 12,
    padding: "4px 0px",
    color: "#555",
    wordBreak: "break-word"
  };

  const getDisplayText = (val) => {
    if (val === null || val === undefined) return "—";
    const strVal = String(val).trim();
    return strVal === "" ? "—" : strVal;
  };

  const handleStandardChange = (newVal) => {
    if (!isEditable) return;
    setValue(newVal);
    if (onFieldChange) onFieldChange({ field: field.field, value: newVal });
  };

  const handleDeepChange = (path, newVal) => {
    if (!isEditable) return;
    const updatedObject = setDeep(value, path, newVal);
    setValue(updatedObject);
    if (onFieldChange) onFieldChange({ field: field.field, value: updatedObject });
  };

  useEffect(() => {
    setValue(isUpdated ? changedValue : getInitialValue(get(field, "listing.value", null)));
  }, [field, changedValue, isUpdated]);
  if (isSuggestiveField) {

    return (

      <div style={{ width: "100%" }}>

        <input
          list={`suggestions-${field.field}`}
          style={inputStyle}
          value={
            value !== null && value !== undefined
              ? String(value)
              : ""
          }
          onFocus={(e) => {

            const input = e.target;

            const current = input.value;

            input.value = "";

            setTimeout(() => {
              input.value = current;
            }, 0);
          }}
          onChange={(e) =>
            handleStandardChange(e.target.value)
          }
          placeholder={placeholder}
          autoComplete="off"
        />

        <datalist id={`suggestions-${field.field}`}>

          {options.map((opt) => (

            <option
              key={String(opt)}
              value={String(opt)}
            />

          ))}

        </datalist>

      </div>
    );
  }
  if (
    isDropdown &&
    !isMultiValueField
  ) {
    let selectedValue = "";
    if (value !== null && value !== undefined) {
      if (Array.isArray(value) && value.length > 0) {
        selectedValue = String(value[0]);
      } else if (typeof value !== "object") {
        selectedValue = String(value);
      }
    }

    if (!isEditable) {
      return <div style={plainTextStyle}>{getDisplayText(selectedValue)}</div>;
    }

    return (
      <select
        style={inputStyle}
        value={selectedValue}
        onChange={(e) => {
          if (!isEditable) return;
          const originalIsArray = Array.isArray(get(field, "listing.value", null));
          const newVal = originalIsArray ? [e.target.value] : e.target.value;

          setValue(newVal);
          if (onFieldChange) onFieldChange({ field: field.field, value: newVal });
        }}
      >
        <option value="">
          {placeholder || "Select"}
        </option>

        {options.map((opt) => (
          <option key={String(opt)} value={String(opt)}>
            {String(opt)}
          </option>
        ))}
      </select>
    );
  }

  const renderDeep = (data, path = [], isObjProp = false) => {
    if (data === null || data === undefined) {

      if (!isEditable) {
        return (
          <div
            style={{
              ...plainTextStyle,
              width: isObjProp ? "60%" : "100%"
            }}
          >
            —
          </div>
        );
      }

      if (isNumberField) {

        return (
          <div style={{ width: isObjProp ? "60%" : "100%" }}>

            <AutoResizeTextarea
              style={{
                ...inputStyle,
                width: "100%",
                marginBottom: 0
              }}
              value=""
              onChange={(e) =>
                handleDeepChange(
                  path,
                  e.target.value
                )
              }
              placeholder={placeholder}
            />

            <div className="mt-1">

              <small className="text-muted">

                Allowed range:

                {" "}

                {minimum !== undefined &&
                  maximum !== undefined && (
                    <>
                      {minimum} - {maximum}
                    </>
                  )}

                {minimum !== undefined &&
                  maximum === undefined && (
                    <>
                      Min: {minimum}
                    </>
                  )}

                {minimum === undefined &&
                  maximum !== undefined && (
                    <>
                      Max: {maximum}
                    </>
                  )}

              </small>

            </div>

          </div>
        );
      }

      return null;
    }
    if (typeof data !== "object") {

      if (!isEditable) {
        return (
          <div
            style={{
              ...plainTextStyle,
              width: isObjProp ? "60%" : "100%"
            }}
          >
            {getDisplayText(data)}
          </div>
        );
      }

      const textValue =
        data !== null && data !== undefined
          ? String(data)
          : "";
      const localIsNumberField =
        (
          actualFieldType === "number" ||
          actualFieldType === "integer"
        ) &&
        !isNaN(Number(textValue)) &&
        textValue !== "";
      const localIsDateField =
        rules.format === "date" ||
        rules.format === "date-time";
      const currentLength =
        textValue.length;
      const currentUtf8Length =
        new TextEncoder()
          .encode(textValue)
          .length;
      const isTooLong =

        (
          maxLength &&
          currentLength > maxLength
        )

        ||

        (
          maxUtf8ByteLength &&
          currentUtf8Length >
          maxUtf8ByteLength
        );
      const numericValue = Number(textValue);

      const isNumberInvalid =
        localIsNumberField &&
        textValue !== "" &&
        (
          (minimum !== undefined &&
            numericValue < minimum) ||

          (maximum !== undefined &&
            numericValue > maximum) ||

          (
            exclusiveMinimum !== undefined &&
            numericValue <= exclusiveMinimum
          ) ||

          (
            exclusiveMaximum !== undefined &&
            numericValue >= exclusiveMaximum
          )
        );
      const isTooShort =
        minLength &&
        currentLength < minLength &&
        currentLength > 0;

      return (
        <div style={{ width: isObjProp ? "60%" : "100%" }}>

          {localIsDateField ? (

            <input
              type="date"
              style={{
                ...inputStyle,
                width: "100%",
                marginBottom: 0
              }}
              value={
                textValue
                  ? textValue.split("T")[0]
                  : ""
              }
              onChange={(e) =>
                handleDeepChange(
                  path,
                  e.target.value
                )
              }
            />

          ) : (

            options.length > 0 ? (

              <>
                <input
                  list={`suggestions-${field.field}`}
                  style={{
                    ...inputStyle,
                    width: "100%",
                    marginBottom: 0
                  }}
                  value={textValue}
                  onFocus={(e) => {

                    const input = e.target;

                    const current = input.value;

                    input.value = "";

                    setTimeout(() => {
                      input.value = current;
                    }, 0);
                  }}
                  onChange={(e) => {

                    let newValue = e.target.value;

                    handleDeepChange(
                      path,
                      newValue
                    );
                  }}
                  placeholder={placeholder}
                  autoComplete="off"
                />

                <datalist id={`suggestions-${field.field}`}>

                  {options.map((opt) => (

                    <option
                      key={String(opt)}
                      value={String(opt)}
                    />

                  ))}

                </datalist>
              </>

            ) : (

              <AutoResizeTextarea
                style={{
                  ...inputStyle,
                  width: "100%",
                  marginBottom: 0,
                  border:
                    isTooLong ||
                      isTooShort ||
                      isNumberInvalid
                      ? "1px solid red"
                      : "1px solid #ccc"
                }}
                value={textValue}
                onChange={(e) => {

                  let newValue = e.target.value;

                  if (localIsNumberField) {

                    const numberRegex =

                      actualFieldType === "integer"

                        ? /^\d*$/

                        : (
                          decimalPlaces === null
                            ? /^\d*\.?\d*$/
                            : new RegExp(
                              `^\\d*\\.?\\d{0,${decimalPlaces}}$`
                            )
                        );

                    if (
                      newValue !== "" &&
                      !numberRegex.test(newValue)
                    ) {
                      return;
                    }
                  }

                  handleDeepChange(
                    path,
                    newValue
                  );
                }}
                placeholder={placeholder}
              />

            ))}

          {!localIsNumberField &&
            (
              maxLength ||
              maxUtf8ByteLength ||
              minLength
            ) && (

              <div className="d-flex justify-content-between">

                <div>

                  {isTooLong && (
                    <small className="text-danger">
                      {
                        maxUtf8ByteLength
                          ? `Maximum ${maxUtf8ByteLength} UTF8 bytes allowed`
                          : `Maximum ${maxLength} characters allowed`
                      }
                    </small>
                  )}



                </div>

                <small
                  className={
                    isTooLong
                      ? "text-danger"
                      : "text-muted"
                  }
                >
                  {currentLength}
                  {
                    maxLength
                      ? ` / ${maxLength}`
                      : (
                        maxUtf8ByteLength
                          ? ` / ${maxUtf8ByteLength} bytes`
                          : ""
                      )
                  }
                </small>

              </div>
            )}

        </div>
      );
    }

    if (Array.isArray(data)) {
      const normalizedValues =
        data
          .filter(
            item =>
              item !== null &&
              item !== undefined &&
              item !== ""
          )
          .map(item =>
            JSON.stringify(item)
          );

      const hasDuplicateValues =
        new Set(normalizedValues).size !==
        normalizedValues.length;
      const maxAllowedItems =
        rules.maxItems !== undefined
          ? rules.maxItems
          : rules.maxUniqueItems;

      const minAllowedItems =
        rules.minItems !== undefined
          ? rules.minItems
          : 1;

      return (

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%"
          }}
        >

          {data.map((item, index) => {

            const isPrimitive =
              typeof item !== "object" ||
              item === null;

            return (

              <div
                key={index}
                style={{
                  marginBottom:
                    isPrimitive
                      ? "4px"
                      : (
                        index < data.length - 1
                          ? "8px"
                          : "0"
                      )
                }}
              >

                {renderDeep(
                  item,
                  [...path, index]
                )}

              </div>
            );
          })}

          {isEditable && (

            <div
              style={{
                display: "flex",
                gap: "12px",
                fontSize: "12px"
              }}
            >

              {(
                !maxAllowedItems ||
                data.length < maxAllowedItems
              ) && (

                  <span
                    style={{
                      color: "#007185",
                      cursor: "pointer"
                    }}
                    onClick={() => {

                      const updated = [
                        ...data,
                        ""
                      ];

                      setValue(updated);

                      if (onFieldChange) {

                        onFieldChange({
                          field: field.field,
                          value: updated
                        });
                      }
                    }}
                  >
                    Add More
                  </span>
                )}

              {data.length > minAllowedItems && (

                <span
                  style={{
                    color: "#007185",
                    cursor: "pointer"
                  }}
                  onClick={() => {

                    const updated =
                      data.slice(0, -1);

                    setValue(updated);

                    if (onFieldChange) {

                      onFieldChange({
                        field: field.field,
                        value: updated
                      });
                    }
                  }}
                >
                  Remove Last
                </span>
              )}

            </div>
          )}
          {hasDuplicateValues && (

            <div
              className="text-danger"
              style={{
                fontSize: "11px",
                marginTop: "4px"
              }}
            >
              Duplicate values are not allowed
            </div>

          )}
        </div>
      );
    }
    if (typeof data === "object") {
      return (
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          {Object.entries(data).map(([k, v]) => {
            const currentPath = [...path, k];

            if (typeof v !== "object" || v === null) {
              return (
                <div key={k} style={{ display: "flex", alignItems: "center", marginBottom: "4px" }}>
                  <span style={{ fontSize: "11px", fontWeight: "500", width: "35%", paddingRight: "5px", color: "#555", wordBreak: "break-word" }}>
                    {k}:
                  </span>
                  {renderDeep(v, currentPath, true)}
                </div>
              );
            }

            return (
              <div key={k} style={{ marginBottom: "6px", backgroundColor: "#fdfdfd", padding: "6px", borderRadius: "4px", border: "1px solid #ebebeb" }}>
                <div style={{ fontSize: "11px", fontWeight: "bold", color: "#333", marginBottom: "4px", textTransform: "capitalize" }}>
                  {k.replace(/_/g, " ")}
                </div>
                <div style={{ paddingLeft: "4px" }}>
                  {renderDeep(v, currentPath)}
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return null;
  };

  if (
    Array.isArray(value) ||
    (value !== null && typeof value === "object")
  ) {
    return renderDeep(value);
  }

  if (!isEditable) {
    return (
      <div style={plainTextStyle}>
        {getDisplayText(value)}
      </div>
    );
  }

  return (
    <div style={{ width: "100%" }}>
      {isDateField ? (

        <input
          type="date"
          style={inputStyle}
          value={
            value
              ? String(value).split("T")[0]
              : ""
          }
          onChange={(e) =>
            handleStandardChange(e.target.value)
          }
        />

      ) : (
        <AutoResizeTextarea
          style={inputStyle}
          value={
            value !== null && value !== undefined
              ? String(value)
              : ""
          }
          onChange={(e) => {

            let newValue = e.target.value;

            // allow only numbers
            if (isNumberField) {

              const numberRegex =

                actualFieldType === "integer"

                  ? /^\d*$/

                  : (
                    decimalPlaces === null
                      ? /^\d*\.?\d*$/
                      : new RegExp(
                        `^\\d*\\.?\\d{0,${decimalPlaces}}$`
                      )
                  );

              if (
                newValue !== "" &&
                !numberRegex.test(newValue)
              ) {
                return;
              }
            }

            handleStandardChange(newValue);
          }}
          placeholder={placeholder}
        />
      )}
      {!isNumberField &&
        (
          maxLength ||
          maxUtf8ByteLength ||
          minLength
        ) && (
          <div
            className="d-flex justify-content-between"
          >

            <div>

              {isTooLong && (
                <small className="text-danger">
                  {
                    maxUtf8ByteLength
                      ? `Maximum ${maxUtf8ByteLength} UTF8 bytes allowed`
                      : `Maximum ${maxLength} characters allowed`
                  }
                </small>
              )}


            </div>

            <small
              className={
                isTooLong
                  ? "text-danger"
                  : "text-muted"
              }
            >
              {currentLength}
              {
                maxLength
                  ? ` / ${maxLength}`
                  : (
                    maxUtf8ByteLength
                      ? ` / ${maxUtf8ByteLength} bytes`
                      : ""
                  )
              }
            </small>

          </div>
        )}
      {isNumberField &&
        minimum !== undefined &&
        (
          actualFieldType === "number" ||
          actualFieldType === "integer"
        ) && (
          <div className="d-flex justify-content-end mt-1">

            <small className="text-muted">

              {minimum !== undefined &&
                maximum !== undefined && (
                  <>
                    {minimum} - {maximum}
                  </>
                )}

              {minimum !== undefined &&
                maximum === undefined && (
                  <>
                    Min: {minimum}
                  </>
                )}

              {minimum === undefined &&
                maximum !== undefined && (
                  <>
                    Max: {maximum}
                  </>
                )}

            </small>

          </div>
        )}
      {isNumberInvalid && (

        <div className="d-flex justify-content-end">


          {exclusiveMinimum !== undefined &&
            numericValue <= exclusiveMinimum && (
              <small className="text-danger">
                Value must be greater than {exclusiveMinimum}
              </small>
            )}

          {exclusiveMaximum !== undefined &&
            numericValue >= exclusiveMaximum && (
              <small className="text-danger">
                Value must be less than {exclusiveMaximum}
              </small>
            )}

          {minimum !== undefined &&
            maximum !== undefined &&
            exclusiveMinimum === undefined &&
            exclusiveMaximum === undefined && (
              <small className="text-danger">
                Value must be between {minimum} - {maximum}
              </small>
            )}

          {minimum !== undefined &&
            maximum === undefined &&
            exclusiveMinimum === undefined && (
              <small className="text-danger">
                Value must be at least {minimum}
              </small>
            )}

          {minimum === undefined &&
            maximum !== undefined &&
            exclusiveMaximum === undefined && (
              <small className="text-danger">
                Maximum value is {maximum}
              </small>
            )}

        </div>

      )}
      {isDateInvalid && (
        <div className="d-flex justify-content-end mt-1">


          <small className="text-danger">
            Invalid date format (YYYY-MM-DD)
          </small>

        </div>
      )}
    </div>
  );
}

export default AmazonProductListingEditableField;